import { AfterContentInit, ChangeDetectionStrategy, Component, ContentChildren, Input, QueryList } from '@angular/core';
import { FloatingButtonMenuProps } from '@bl/components';
import { type SnippetService } from '@bl/components/dist/types';
import { SvelteService } from '@bl/components/dist/wrappers';
import { BlFrontendBaseComponent } from './bl-frontend-base.component';
import { SnippetDirective } from './snippet-directives/snippet.directive';
import { SnippetDirectiveType } from './snippet-directives/types';

@Component({
  selector: 'bl-frontend-floating-button-menu',
  template: '<ng-content select="[snippet]"></ng-content>',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class BlFrontendFloatingButtonMenuComponent
  extends BlFrontendBaseComponent<FloatingButtonMenuProps>
  implements AfterContentInit
{
  @ContentChildren(SnippetDirective) snippets?: QueryList<SnippetDirectiveType>;

  @Input() id: FloatingButtonMenuProps['id'] = '';
  @Input() iconName: FloatingButtonMenuProps['name'];
  @Input() variant: FloatingButtonMenuProps['variant']; // default to 'tonal'
  @Input() iconVariant: FloatingButtonMenuProps['iconVariant'];
  @Input() disabled: FloatingButtonMenuProps['disabled'] = false;
  @Input() size: FloatingButtonMenuProps['size']; // default to 'lg'

  @Input() menuItems: FloatingButtonMenuProps['menuItems'] = [];
  @Input() menuTarget?: FloatingButtonMenuProps['menuTarget'];
  @Input() maxMenuWidth?: FloatingButtonMenuProps['maxMenuWidth'];
  @Input() maxMenuHeight?: FloatingButtonMenuProps['maxMenuHeight'];

  @Input() ariaLabel?: FloatingButtonMenuProps['ariaLabel'];

  ngAfterContentInit(): void {
    if (!this.snippets || this.snippets.length === 0) {
      return;
    }
    this.updateMenuSnippet();
  }

  protected override getComponentName(): string {
    return 'FloatingButtonMenu';
  }

  protected override getProps(): FloatingButtonMenuProps {
    return {
      id: this.id,
      name: this.iconName,
      variant: this.variant,
      iconVariant: this.iconVariant,
      disabled: this.disabled,
      size: this.size,
      menuItems: this.menuItems,
      menuTarget: this.menuTarget,
      maxMenuWidth: this.maxMenuWidth,
      maxMenuHeight: this.maxMenuHeight,
      ariaLabel: this.ariaLabel,
    };
  }

  private snippetService = this.getService<SnippetService>(SvelteService.SnippetService);

  private createSvelteSnippet(template: string) {
    return this.snippetService.createSvelteSnippet(template);
  }

  private updateMenuSnippet() {
    let propsToUpdate = {};

    const menuHeader = this.getMenuHeaderSnippet();
    if (menuHeader) {
      propsToUpdate = { ...propsToUpdate, menuHeader: this.createSvelteSnippet(menuHeader.getHtmlContent()) };
      menuHeader.hideElement();
    }

    const menuFooter = this.getMenuFooterSnippet();
    if (menuFooter) {
      propsToUpdate = { ...propsToUpdate, menuFooter: this.createSvelteSnippet(menuFooter.getHtmlContent()) };
      menuFooter.hideElement();
    }

    this.updateProps(propsToUpdate);
  }

  private getMenuHeaderSnippet(): SnippetDirectiveType | undefined {
    return this.snippets?.find((snippet) => snippet.getSnippetSelector() === 'menuHeader');
  }

  private getMenuFooterSnippet(): SnippetDirectiveType | undefined {
    return this.snippets?.find((snippet) => snippet.getSnippetSelector() === 'menuFooter');
  }
}
